/**
 * src/client/ui/screens.ts
 *
 * DOM screens: Start, Game-over (+ inline nickname entry), Leaderboard.
 *
 * SECURITY: Nicknames rendered with textContent/createTextNode ONLY — never innerHTML.
 * No XSS vector on stored nicknames.
 */

import type { GameResult } from "../game/loop.js";
import type { GetLeaderboardResponse } from "../../../shared/schema.js";
import {
  submitScore,
  getLeaderboard,
  getPersonalBest,
  maybeUpdatePersonalBest,
  getDeviceToken,
} from "../net/api.js";

// ---------------------------------------------------------------------------
// SVG icons (inline, no emoji for play/trophy per spec)
// ---------------------------------------------------------------------------

const ICON_PLAY = `<svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`;
const ICON_TROPHY = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18" aria-hidden="true"><path d="M6 9a6 6 0 0012 0V3H6v6zM4 3h2v4a3 3 0 01-3-3zM20 3h-2v4a3 3 0 003-3zM9 21h6M12 15v6"/></svg>`;
// ICON_SOUND_ON available if audio is re-enabled
const _ICON_SOUND_ON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="19" height="19" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg>`;
const ICON_SOUND_OFF = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="19" height="19" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>`;
const ICON_BACK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="19" height="19" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>`;
const ICON_CLOCK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15" aria-hidden="true"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l3 2M9 2h6"/></svg>`;
const ICON_ALERT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18" aria-hidden="true"><path d="M10.3 3.9L2.4 18a2 2 0 001.7 3h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>`;
const ICON_CHECK = `<svg viewBox="0 0 24 24" fill="none" stroke="#b4ff3d" stroke-width="2.5" width="14" height="14" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>`;
const ICON_BOLT = `<svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M13 2L4 14h6l-1 8 9-12h-6z"/></svg>`;

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

type Screen = "start" | "game" | "gameover" | "leaderboard";

let currentScreen: Screen = "start";
let _gameContainer: HTMLElement | null = null;

export function mountScreens(root: HTMLElement): void {
  root.innerHTML = "";
  showStartScreen(root);
}

// ---------------------------------------------------------------------------
// HUD update (called by game loop)
// ---------------------------------------------------------------------------

export function updateHUD(
  lives: number,
  gold: number,
  wave: number,
  totalWaves: number,
): void {
  const livesEl = document.getElementById("hud-lives");
  const goldEl = document.getElementById("hud-gold");
  const waveEl = document.getElementById("hud-wave");
  if (livesEl) livesEl.textContent = String(lives);
  if (goldEl) goldEl.textContent = String(gold);
  if (waveEl) {
    waveEl.textContent = `${wave}`;
    const sub = document.getElementById("hud-wave-total");
    if (sub) sub.textContent = `/${totalWaves}`;
  }
}

// ---------------------------------------------------------------------------
// Start Screen
// ---------------------------------------------------------------------------

function showStartScreen(root: HTMLElement): void {
  currentScreen = "start";
  root.innerHTML = "";

  const el = document.createElement("div");
  el.className = "s-start screen";
  el.setAttribute("role", "main");

  const pb = getPersonalBest();
  const pbHtml = pb
    ? `<div class="pb-chip">
        ${ICON_TROPHY}
        <span class="lbl">Your best</span>
        <span class="val">Wave ${pb.wave} · <em>${pb.score.toLocaleString()}</em></span>
       </div>`
    : "";

  el.innerHTML = `
    <div class="start-top">
      <div class="brand">
        <span class="glyph"><svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 20V9l9-6 9 6v11M8 20v-6h8v6"/></svg></span>
        <span class="wordmark">NEON<b>TD</b></span>
      </div>
      <button class="iconbtn" id="sound-toggle" aria-label="Sound off — tap to unmute">
        ${ICON_SOUND_OFF}
      </button>
    </div>

    <div class="start-hero">
      <div class="creator">PixelPunk's Arena</div>
      <h2><span class="l1">DEFEND THE</span><span class="l2">GRID</span></h2>
      <p class="tagline">Ten waves. Three towers. One shot at this week's board.</p>
      ${pbHtml}
    </div>

    <div class="start-actions">
      <button class="btn btn-primary btn-block pulse" id="play-btn" aria-label="Play game">
        ${ICON_PLAY} Play Now
      </button>
      <div class="mini-row">
        <button class="linkbtn" id="leaderboard-btn">
          ${ICON_TROPHY} Leaderboard
        </button>
        <button class="linkbtn" id="howtoplay-btn">How to play</button>
      </div>
    </div>

    <span class="hero-accent a1" aria-hidden="true"></span>
    <span class="hero-accent a2" aria-hidden="true"></span>
    <span class="hero-accent a3" aria-hidden="true"></span>
  `;

  root.appendChild(el);

  document.getElementById("play-btn")?.addEventListener("click", () => {
    showGameScreen(root);
  });

  document.getElementById("leaderboard-btn")?.addEventListener("click", () => {
    showLeaderboardScreen(root, null, null);
  });

  // How to play: simple inline tip (no full screen needed)
  document.getElementById("howtoplay-btn")?.addEventListener("click", () => {
    const tip = document.getElementById("howtoplay-tip");
    if (tip) {
      tip.remove();
      return;
    }
    const t = document.createElement("div");
    t.id = "howtoplay-tip";
    t.className = "howtoplay-tip";
    t.setAttribute("role", "dialog");
    t.setAttribute("aria-modal", "true");
    t.setAttribute("aria-label", "How to play");
    const content = document.createElement("div");
    content.className = "howtoplay-content";

    const title = document.createElement("h3");
    title.textContent = "How to Play";
    content.appendChild(title);

    const steps = [
      "Tap empty slots on the map to place towers",
      "Three tower types: Pulse (fast), Splash (area), Laser (long range)",
      "Stop enemies reaching the base — you have 20 lives",
      "Send waves early for bonus score and gold",
      "Survive 10 waves to win. Post your score to the weekly board!",
    ];

    const ul = document.createElement("ul");
    for (const step of steps) {
      const li = document.createElement("li");
      li.textContent = step;
      ul.appendChild(li);
    }
    content.appendChild(ul);

    const closeBtn = document.createElement("button");
    closeBtn.className = "btn btn-ghost";
    closeBtn.textContent = "Got it";
    closeBtn.addEventListener("click", () => t.remove());
    content.appendChild(closeBtn);

    t.appendChild(content);
    root.appendChild(t);
  });
}

// ---------------------------------------------------------------------------
// Game Screen
// ---------------------------------------------------------------------------

function showGameScreen(root: HTMLElement): void {
  currentScreen = "game";
  root.innerHTML = "";

  const el = document.createElement("div");
  el.className = "s-game screen";
  // Force explicit dimensions to ensure containing block is established
  el.style.cssText = "position:absolute;inset:0;width:100%;height:100%;";

  // Canvas (fills entire game area)
  const canvas = document.createElement("canvas");
  canvas.id = "game-canvas";
  canvas.setAttribute("aria-label", "Game battlefield");
  canvas.style.cssText =
    "position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:none;";

  // Game container (canvas + bloom overlay live here)
  const gameDiv = document.createElement("div");
  gameDiv.id = "game-container";
  gameDiv.style.cssText = "position:absolute;inset:0;width:100%;height:100%;";
  gameDiv.appendChild(canvas);

  // Build menu (hidden by default)
  const buildMenu = document.createElement("div");
  buildMenu.id = "build-menu";
  buildMenu.className = "build-menu";
  buildMenu.style.display = "none";
  buildMenu.setAttribute("role", "menu");
  buildMenu.setAttribute("aria-label", "Tower build options");

  // HUD layer
  const hud = document.createElement("div");
  hud.className = "hud-layer";
  hud.setAttribute("aria-live", "polite");
  hud.setAttribute("aria-atomic", "false");

  hud.innerHTML = `
    <div>
      <div class="hud-top">
        <div class="stat lives">
          <span class="ic"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#ff4d6d" stroke-width="2" aria-hidden="true"><path d="M12 21C7 17 3 13.5 3 9a4.5 4.5 0 019-1 4.5 4.5 0 019 1c0 4.5-4 8-9 12z"/></svg></span>
          <span class="meta"><span class="k">Lives</span><span class="v num" id="hud-lives">20</span></span>
        </div>
        <div class="stat gold">
          <span class="ic"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#ffd23f" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="8"/><path d="M12 8v8M9.5 10h3.2a1.5 1.5 0 010 3H9.5"/></svg></span>
          <span class="meta"><span class="k">Gold</span><span class="v v-gold num" id="hud-gold">150</span></span>
        </div>
        <div class="stat wave">
          <span class="ic"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#53eafd" stroke-width="2" aria-hidden="true"><path d="M2 12c2-4 4-4 6 0s4 4 6 0 4-4 6 0"/></svg></span>
          <span class="meta"><span class="k">Wave</span><span class="v num"><span id="hud-wave">0</span><span id="hud-wave-total" style="color:var(--muted);font-size:.75em">/10</span></span></span>
        </div>
      </div>
    </div>

    <div class="hud-bottom">
      <div class="toggle-row">
        <button class="toggle" id="low-glow-toggle" aria-pressed="false" aria-label="Low-glow mode">
          <span class="sw"></span>Low-Glow
        </button>
        <button class="iconbtn" id="sound-btn" aria-label="Sound off — tap to unmute">
          ${ICON_SOUND_OFF}
        </button>
        <button class="iconbtn" id="pause-btn" aria-label="Pause game">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="19" height="19" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
        </button>
      </div>
      <div class="hud-controls">
        <div class="tower-tray" role="group" aria-label="Tower build tray">
          <button class="tower-slot" id="tray-pulse" aria-label="Pulse tower, 80 gold">
            <svg class="tw" viewBox="0 0 24 24" fill="none" stroke="#53eafd" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="9" opacity=".4"/></svg>
            <span class="cost">80</span>
          </button>
          <button class="tower-slot" id="tray-splash" aria-label="Splash tower, 120 gold">
            <svg class="tw" viewBox="0 0 24 24" fill="none" stroke="#e12afb" stroke-width="2" aria-hidden="true"><path d="M12 3l3 6 6 .9-4.3 4.2 1 6L12 17.8 6.3 20l1-6L3 9.9 9 9z"/></svg>
            <span class="cost">120</span>
          </button>
          <button class="tower-slot" id="tray-laser" aria-label="Laser tower, 200 gold">
            <svg class="tw" viewBox="0 0 24 24" fill="none" stroke="#ffd23f" stroke-width="2" aria-hidden="true"><path d="M12 2v6M12 16v6M4 12h4M16 12h4"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
            <span class="cost">200</span>
          </button>
        </div>
        <div class="send-wave">
          <span class="bonus num" id="early-bonus-label" aria-live="polite">+0 bonus</span>
          <button class="btn-send" id="send-early-btn" aria-label="Send next wave early for bonus">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true"><path d="M5 4l12 8-12 8V4zM19 5v14"/></svg>
            Send Early
          </button>
        </div>
      </div>
    </div>
  `;

  el.appendChild(gameDiv);
  el.appendChild(buildMenu);
  el.appendChild(hud);
  root.appendChild(el);

  _gameContainer = gameDiv;

  // Lazy import to avoid circular at module level
  void import("../game/loop.js").then(({ GameLoop }) => {
    // Use requestAnimationFrame to ensure DOM has laid out before measuring
    requestAnimationFrame(() => {
      // Use clientWidth/clientHeight as fallback since getBoundingClientRect
      // may return 0 for position:absolute children in headless environments
      const rect = canvas.getBoundingClientRect();
      const W = Math.round(rect.width) || canvas.clientWidth || canvas.offsetWidth || root.clientWidth || 390;
      const H = Math.round(rect.height) || canvas.clientHeight || canvas.offsetHeight || root.clientHeight || 844;
      canvas.width = W;
      canvas.height = H;

      const loop = new GameLoop(
        canvas,
        gameDiv,
        (result) => {
          showGameOverScreen(root, result);
        },
        false,
      );

      loop.start();

      // Low-glow toggle
      const lgToggle = document.getElementById(
        "low-glow-toggle",
      ) as HTMLButtonElement | null;
      lgToggle?.addEventListener("click", () => {
        const isOn = lgToggle.getAttribute("aria-pressed") === "true";
        lgToggle.setAttribute("aria-pressed", isOn ? "false" : "true");
        loop.setLowGlow(!isOn);
      });

      // Send early
      document.getElementById("send-early-btn")?.addEventListener("click", () => {
        loop.sendWaveEarly();
      });

      // Pause
      document.getElementById("pause-btn")?.addEventListener("click", () => {
        const overlay = document.createElement("div");
        overlay.className = "pause-overlay";
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");
        overlay.setAttribute("aria-label", "Game paused");

        const inner = document.createElement("div");
        inner.className = "pause-inner";

        const title = document.createElement("h2");
        title.textContent = "Paused";
        title.className = "pause-title";

        const resumeBtn = document.createElement("button");
        resumeBtn.className = "btn btn-primary";
        resumeBtn.textContent = "Resume";
        resumeBtn.addEventListener("click", () => {
          overlay.remove();
        });

        const quitBtn = document.createElement("button");
        quitBtn.className = "btn btn-ghost";
        quitBtn.textContent = "Quit to Menu";
        quitBtn.addEventListener("click", () => {
          loop.stop();
          showStartScreen(root);
        });

        inner.appendChild(title);
        inner.appendChild(resumeBtn);
        inner.appendChild(quitBtn);
        overlay.appendChild(inner);
        el.appendChild(overlay);
      });
    }); // end requestAnimationFrame
  }); // end import().then
}

// ---------------------------------------------------------------------------
// Game Over Screen
// ---------------------------------------------------------------------------

function showGameOverScreen(root: HTMLElement, result: GameResult): void {
  currentScreen = "gameover";
  root.innerHTML = "";

  maybeUpdatePersonalBest(result.score, result.wave);

  const el = document.createElement("div");
  el.className = "s-over screen";
  el.setAttribute("role", "main");

  const isWin = result.won;
  const resultLabel = isWin ? "Grid Defended!" : "Defenses Breached";
  const resultClass = isWin ? "result-pill win" : "result-pill";

  // Score with formatted thousands
  const scoreStr = result.score.toLocaleString();
  const firstPart = scoreStr.slice(0, Math.max(1, scoreStr.indexOf(",") === -1 ? 2 : scoreStr.indexOf(",")));
  const restPart = scoreStr.slice(firstPart.length);

  el.innerHTML = `
    <div class="over-hero">
      <span class="${resultClass}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16" aria-hidden="true">
          ${isWin ? '<path d="M20 6L9 17l-5-5"/>' : '<path d="M18 6L6 18M6 6l12 12"/>'}
        </svg>
        ${resultLabel}
      </span>
      <div class="score-label">Final Score</div>
      <div class="score num"><span class="glow-u">${firstPart}</span>${restPart}</div>
      <div class="context">Reached <b>Wave ${result.wave} of 10</b></div>
    </div>

    <div class="stat-triplet">
      <div class="stat-cell c1"><div class="v num">${result.wave}</div><div class="k">Wave</div></div>
      <div class="stat-cell c2"><div class="v num">${result.kills}</div><div class="k">Kills</div></div>
      <div class="stat-cell c3"><div class="v num">×${result.earlySends}</div><div class="k">Early Sends</div></div>
    </div>
  `;

  // Entry form (inline, not a separate element)
  const entry = document.createElement("div");
  entry.className = "entry";
  entry.id = "score-entry";

  root.appendChild(el);
  el.appendChild(entry);

  renderEntryForm(entry, result, root);
}

function renderEntryForm(
  entry: HTMLElement,
  result: GameResult,
  root: HTMLElement,
): void {
  entry.innerHTML = "";
  entry.className = "entry";

  const title = document.createElement("h3");
  title.textContent = "Claim your spot";
  entry.appendChild(title);

  const hint = document.createElement("p");
  hint.className = "hint";
  hint.innerHTML = `Enter a nickname to post this score to <em>PixelPunk's weekly board</em>.`;
  entry.appendChild(hint);

  // Error note (hidden by default)
  const errNote = document.createElement("div");
  errNote.className = "err-note";
  errNote.setAttribute("role", "alert");
  errNote.setAttribute("aria-live", "assertive");
  errNote.innerHTML = `${ICON_ALERT}<span id="err-note-text"></span>`;
  errNote.style.display = "none";
  entry.appendChild(errNote);

  // Nick field
  const nickField = document.createElement("div");
  nickField.className = "nick-field";

  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = 16;
  input.setAttribute("aria-label", "Nickname, up to 16 characters");
  input.setAttribute("spellcheck", "false");
  input.setAttribute("autocomplete", "off");
  input.placeholder = "Enter nickname…";
  input.value = "";

  const counter = document.createElement("span");
  counter.className = "count num";
  counter.textContent = "0/16";

  nickField.appendChild(input);
  nickField.appendChild(counter);
  entry.appendChild(nickField);

  // Validity line
  const validity = document.createElement("div");
  validity.className = "validity";
  validity.setAttribute("aria-live", "polite");
  entry.appendChild(validity);

  // Would-rank line
  const wouldRank = document.createElement("div");
  wouldRank.className = "would-rank";
  entry.appendChild(wouldRank);

  // Actions
  const actions = document.createElement("div");
  actions.className = "actions";

  const submitBtn = document.createElement("button");
  submitBtn.className = "btn btn-primary btn-block";
  submitBtn.textContent = "Submit Score";
  submitBtn.disabled = true;

  const subActions = document.createElement("div");
  subActions.className = "sub-actions";

  const tryAgainBtn = document.createElement("button");
  tryAgainBtn.className = "btn btn-ghost";
  tryAgainBtn.style.flex = "1";
  tryAgainBtn.textContent = "Try Again";

  const shareBtn = document.createElement("button");
  shareBtn.className = "linkbtn";
  shareBtn.textContent = "Share";

  subActions.appendChild(tryAgainBtn);
  subActions.appendChild(shareBtn);
  actions.appendChild(submitBtn);
  actions.appendChild(subActions);
  entry.appendChild(actions);

  // Auto-focus input
  requestAnimationFrame(() => input.focus());

  // Input event
  input.addEventListener("input", () => {
    const raw = input.value;
    const trimmed = raw.trim();
    const len = trimmed.length;

    counter.textContent = `${raw.length}/16`;
    counter.className = `count num${raw.length >= 14 ? " warn" : ""}`;

    if (len === 0) {
      validity.innerHTML = "";
      submitBtn.disabled = true;
    } else if (len > 16) {
      validity.innerHTML = `<span style="color:var(--danger)">Too long (max 16 chars)</span>`;
      submitBtn.disabled = true;
    } else {
      validity.innerHTML = `${ICON_CHECK}<span style="color:var(--lime)">Looks good</span>`;
      submitBtn.disabled = false;
    }
  });

  // Submit
  submitBtn.addEventListener("click", async () => {
    const nickname = input.value.trim();
    if (!nickname || nickname.length > 16) return;

    submitBtn.disabled = true;
    submitBtn.textContent = "Submitting…";
    errNote.style.display = "none";
    entry.classList.remove("state-error");

    const res = await submitScore({
      nickname,
      score: result.score,
      wave: result.wave,
      durationMs: result.durationMs,
    });

    if (res.ok) {
      showLeaderboardScreen(root, res.data, nickname);
    } else {
      // Keep player on game-over with retry — never lose score silently
      submitBtn.disabled = false;
      submitBtn.className = "btn btn-magenta btn-block";
      submitBtn.textContent = "Retry Submit";
      entry.classList.add("state-error");
      errNote.style.display = "flex";
      const errText = document.getElementById("err-note-text");
      if (errText) {
        errText.textContent =
          res.status === 503
            ? "Board is busy right now (503). Your score is saved — tap retry."
            : res.status === 429
              ? "Too many submissions — please wait a moment and retry."
              : `Submit failed: ${res.message} Tap retry.`;
      }
      nickField.classList.add("error-focus");
    }
  });

  // Try again
  tryAgainBtn.addEventListener("click", () => {
    showGameScreen(root);
  });

  // Share
  shareBtn.addEventListener("click", () => {
    const text = `I reached Wave ${result.wave} and scored ${result.score.toLocaleString()} on Neon TD! Can you beat me? ${location.href}`;
    if (navigator.share) {
      void navigator.share({ title: "Neon TD", text, url: location.href });
    } else {
      void navigator.clipboard.writeText(text).then(() => {
        shareBtn.textContent = "Copied!";
        setTimeout(() => {
          shareBtn.textContent = "Share";
        }, 2000);
      });
    }
  });
}

// ---------------------------------------------------------------------------
// Leaderboard Screen
// ---------------------------------------------------------------------------

import type { LeaderboardResponse } from "../../../shared/schema.js";

export function showLeaderboardScreen(
  root: HTMLElement,
  postResult: LeaderboardResponse | null,
  playerNickname: string | null,
): void {
  currentScreen = "leaderboard";
  root.innerHTML = "";

  const el = document.createElement("div");
  el.className = "s-board screen";
  el.setAttribute("role", "main");

  // Header
  const head = document.createElement("div");
  head.className = "board-head";

  head.innerHTML = `
    <div class="title-row">
      <button class="iconbtn" id="board-back" aria-label="Back to start">
        ${ICON_BACK}
      </button>
      <h2>Weekly Board<small id="board-week-label">PixelPunk's Arena</small></h2>
      <button class="iconbtn" id="board-share" aria-label="Share leaderboard">
        ${ICON_TROPHY}
      </button>
    </div>
    <div class="board-meta">
      <div class="week-toggle" role="tablist" aria-label="Week selector">
        <button role="tab" aria-selected="true" id="tab-current">This week</button>
        <button role="tab" aria-selected="false" id="tab-last">Last week</button>
      </div>
      <div class="countdown" aria-live="off">
        ${ICON_CLOCK}
        <span class="lbl">Resets</span>
        <span class="t num" id="countdown-val">—</span>
      </div>
    </div>
  `;

  const listContainer = document.createElement("div");
  listContainer.className = "board-list";
  listContainer.id = "board-list";

  // Skeleton loading rows
  listContainer.innerHTML = `
    <div class="sk-rows" aria-hidden="true" id="sk-rows">
      <div class="sk"></div>
      <div class="sk" style="animation-delay:.15s"></div>
      <div class="sk" style="animation-delay:.3s"></div>
      <div class="sk" style="animation-delay:.45s"></div>
      <div class="sk" style="animation-delay:.6s"></div>
    </div>
  `;

  const pinnedYou = document.createElement("div");
  pinnedYou.id = "pinned-you";

  const foot = document.createElement("div");
  foot.className = "board-foot";
  foot.id = "board-foot";

  el.appendChild(head);
  el.appendChild(listContainer);
  el.appendChild(pinnedYou);
  el.appendChild(foot);
  root.appendChild(el);

  // Back button
  document.getElementById("board-back")?.addEventListener("click", () => {
    showStartScreen(root);
  });

  // Share board
  document.getElementById("board-share")?.addEventListener("click", () => {
    const text = `Check out the Neon TD weekly leaderboard! ${location.href}`;
    if (navigator.share) {
      void navigator.share({ title: "Neon TD Leaderboard", text, url: location.href });
    } else {
      void navigator.clipboard.writeText(location.href);
    }
  });

  // Week tabs
  let currentWeek: "current" | "last" = "current";

  const tabCurrent = document.getElementById("tab-current") as HTMLButtonElement | null;
  const tabLast = document.getElementById("tab-last") as HTMLButtonElement | null;

  tabCurrent?.addEventListener("click", () => {
    if (currentWeek === "current") return;
    currentWeek = "current";
    tabCurrent.setAttribute("aria-selected", "true");
    tabLast?.setAttribute("aria-selected", "false");
    void loadBoard("current", postResult, playerNickname);
  });

  tabLast?.addEventListener("click", () => {
    if (currentWeek === "last") return;
    currentWeek = "last";
    tabLast.setAttribute("aria-selected", "true");
    tabCurrent?.setAttribute("aria-selected", "false");
    void loadBoard("last", null, null);
  });

  // If we have fresh post result, display immediately then also fetch to get full board
  if (postResult) {
    renderBoard(postResult, playerNickname, "current");
  } else {
    void loadBoard("current", null, null);
  }
}

async function loadBoard(
  week: "current" | "last",
  freshPost: LeaderboardResponse | null,
  playerNickname: string | null,
): Promise<void> {
  // Show skeleton
  const listEl = document.getElementById("board-list");
  const pinnedEl = document.getElementById("pinned-you");
  if (listEl) {
    listEl.innerHTML = `<div class="sk-rows" aria-hidden="true">
      <div class="sk"></div>
      <div class="sk" style="animation-delay:.15s"></div>
      <div class="sk" style="animation-delay:.3s"></div>
    </div>`;
  }
  if (pinnedEl) pinnedEl.innerHTML = "";

  if (freshPost && week === "current") {
    renderBoard(freshPost, playerNickname, week);
    return;
  }

  const result = await getLeaderboard(week);
  if (!result.ok) {
    if (listEl) {
      listEl.innerHTML = "";
      const err = document.createElement("div");
      err.className = "board-empty";
      const h3 = document.createElement("h3");
      h3.textContent = "Couldn't load board";
      const p = document.createElement("p");
      p.textContent = "Check your connection and try again.";
      err.appendChild(h3);
      err.appendChild(p);
      listEl.appendChild(err);
    }
    return;
  }

  renderBoard(result.data, playerNickname, week);
}

function renderBoard(
  data: GetLeaderboardResponse | LeaderboardResponse,
  playerNickname: string | null,
  week: "current" | "last",
): void {
  const listEl = document.getElementById("board-list");
  const pinnedEl = document.getElementById("pinned-you");
  const weekLabel = document.getElementById("board-week-label");
  const countdownVal = document.getElementById("countdown-val");
  const foot = document.getElementById("board-foot");

  // Week label
  if (weekLabel) {
    weekLabel.textContent = `PixelPunk's Arena · ${data.week}`;
  }

  // Countdown
  if (countdownVal && data.resetsAt) {
    updateCountdown(countdownVal, data.resetsAt);
    const interval = setInterval(() => {
      if (!document.body.contains(countdownVal)) {
        clearInterval(interval);
        return;
      }
      updateCountdown(countdownVal, data.resetsAt);
    }, 60000);
  }

  const rank =
    "rank" in data && typeof data.rank === "number" ? data.rank : null;

  if (!listEl) return;
  listEl.innerHTML = "";

  if (data.top.length === 0) {
    // Empty board state
    const empty = document.createElement("div");
    empty.className = "board-empty";

    const ic = document.createElement("div");
    ic.className = "ic";
    ic.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="#53eafd" stroke-width="1.5" width="34" height="34" aria-hidden="true"><path d="M6 9a6 6 0 0012 0V3H6v6zM4 3h2v4a3 3 0 01-3-3zM20 3h-2v4a3 3 0 003-3zM9 21h6M12 15v6"/></svg>`;

    const h3 = document.createElement("h3");
    h3.textContent = "Fresh Week, Empty Board";

    const p = document.createElement("p");
    p.textContent = "No scores yet this week. Play a run and you'll be first on the board.";

    const playBtn = document.createElement("button");
    playBtn.className = "btn btn-primary";
    playBtn.innerHTML = `${ICON_PLAY} Be the First`;
    playBtn.addEventListener("click", () => {
      const appEl = document.getElementById("app");
      if (appEl) showGameScreen(appEl);
    });

    empty.appendChild(ic);
    empty.appendChild(h3);
    empty.appendChild(p);
    empty.appendChild(playBtn);
    listEl.appendChild(empty);

    if (pinnedEl) pinnedEl.innerHTML = "";
    if (foot) {
      foot.innerHTML = "";
      const link = document.createElement("button");
      link.className = "linkbtn";
      link.style.color = "var(--muted)";
      link.textContent = `View last week's winners`;
      link.addEventListener("click", () => {
        const tabLast = document.getElementById("tab-last") as HTMLButtonElement | null;
        tabLast?.click();
      });
      foot.appendChild(link);
    }
    return;
  }

  // Medal icons for top 3
  const medals = ["🥇", "🥈", "🥉"];
  const rankClasses = ["r1", "r2", "r3"];

  // Determine if player is in top 20
  const deviceToken = getDeviceToken();
  let playerRank: number | null = rank;
  let playerRow: { nick: string; score: number; wave: number; rank: number } | null = null;

  let playerInTop = false;

  data.top.forEach((entry: import("../../../shared/schema.js").ScoreEntry, idx: number) => {
    const rowRank = idx + 1;
    const row = document.createElement("div");
    row.className = `row${rankClasses[idx] !== undefined ? " " + rankClasses[idx] : ""}`;

    // Check if this is the player's entry
    const isPlayer = playerNickname !== null && entry.nickname === playerNickname;
    if (isPlayer) {
      playerInTop = true;
      playerRank = rowRank;
      row.classList.add("you-slide");
    }

    // Rank / medal
    if (idx < 3) {
      const medal = document.createElement("span");
      medal.className = "medal";
      medal.textContent = medals[idx] ?? "";
      row.appendChild(medal);
    } else {
      const rankEl = document.createElement("span");
      rankEl.className = "rank num";
      rankEl.textContent = String(rowRank);
      row.appendChild(rankEl);
    }

    // Avatar
    const av = document.createElement("span");
    av.className = "av";
    const initials = entry.nickname.slice(0, 2).toUpperCase();
    av.textContent = initials; // textContent — no XSS
    av.style.background = avatarGradient(entry.nickname);
    row.appendChild(av);

    // Nick + wave (textContent only — NO innerHTML for nickname)
    const nickDiv = document.createElement("span");
    nickDiv.className = "nick";

    const nameEl = document.createElement("span");
    nameEl.className = "n";
    nameEl.textContent = entry.nickname; // SAFE: textContent only

    if (isPlayer) {
      const youTag = document.createElement("span");
      youTag.className = "you-tag";
      youTag.textContent = "YOU";
      nameEl.appendChild(youTag);
    }

    const waveEl = document.createElement("span");
    waveEl.className = "w";
    waveEl.textContent = `Wave ${entry.wave}`;

    nickDiv.appendChild(nameEl);
    nickDiv.appendChild(waveEl);
    row.appendChild(nickDiv);

    // Score
    const scoreEl = document.createElement("span");
    scoreEl.className = "score num";
    scoreEl.textContent = entry.score.toLocaleString(); // safe
    scoreEl.innerHTML += ` ${ICON_BOLT}`; // bolt icon is safe static SVG
    row.appendChild(scoreEl);

    listEl.appendChild(row);

    // Store player row data
    if (isPlayer) {
      playerRow = {
        nick: entry.nickname,
        score: entry.score,
        wave: entry.wave,
        rank: rowRank,
      };
    }
  });

  // Pinned "you" row outside top 20
  if (pinnedEl) {
    if (!playerInTop && playerNickname && playerRank !== null) {
      pinnedEl.innerHTML = "";
      const pinnedRow = document.createElement("div");
      pinnedRow.className = "row pinned-you you-slide";
      pinnedRow.setAttribute("aria-label", `Your rank: #${playerRank}`);

      const rankEl = document.createElement("span");
      rankEl.className = "rank num";
      rankEl.textContent = `#${playerRank}`;

      const av = document.createElement("span");
      av.className = "av";
      av.textContent = playerNickname.slice(0, 2).toUpperCase();
      av.style.cssText = `background:linear-gradient(135deg,#53eafd,#1fb9d4);color:var(--on-accent)`;

      const nickDiv = document.createElement("span");
      nickDiv.className = "nick";
      const nameEl = document.createElement("span");
      nameEl.className = "n";
      nameEl.textContent = playerNickname; // SAFE: textContent

      const youTag = document.createElement("span");
      youTag.className = "you-tag";
      youTag.textContent = "YOU";
      nameEl.appendChild(youTag);

      const waveSubEl = document.createElement("span");
      waveSubEl.className = "w";
      waveSubEl.textContent = "just now";

      nickDiv.appendChild(nameEl);
      nickDiv.appendChild(waveSubEl);

      pinnedRow.appendChild(rankEl);
      pinnedRow.appendChild(av);
      pinnedRow.appendChild(nickDiv);
      pinnedEl.appendChild(pinnedRow);
    } else {
      pinnedEl.innerHTML = "";
    }
  }

  void playerRow;
  void deviceToken;
  void week;

  // Footer
  if (foot) {
    foot.innerHTML = "";
    const total = data.total ?? data.top.length;
    const footText = document.createElement("span");
    footText.className = "linkbtn";
    footText.style.color = "var(--muted)";
    footText.textContent = `Showing top ${data.top.length} of ${total} players`;
    foot.appendChild(footText);
  }
}

// ---------------------------------------------------------------------------
// Countdown timer display
// ---------------------------------------------------------------------------

function updateCountdown(el: HTMLElement, resetsAt: string): void {
  const now = Date.now();
  const target = new Date(resetsAt).getTime();
  const diff = Math.max(0, target - now);

  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);

  if (d > 0) {
    el.textContent = `${d}d ${h}h ${m}m`;
  } else if (h > 0) {
    el.textContent = `${h}h ${m}m`;
  } else {
    el.textContent = `${m}m`;
  }
}

// ---------------------------------------------------------------------------
// Avatar gradient (deterministic from nickname)
// ---------------------------------------------------------------------------

function avatarGradient(nickname: string): string {
  let hash = 0;
  for (let i = 0; i < nickname.length; i++) {
    hash = ((hash << 5) - hash + nickname.charCodeAt(i)) | 0;
  }
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${h1},80%,55%), hsl(${h2},70%,40%))`;
}

// Keep currentScreen accessible
export { currentScreen };
